import { callApiWithoutDispatch } from '../../../../../../../utils/apiUtils';
import {
  getRequiredFieldsGsnApi,
  createBankAccountGsnApi,
  getPayoutFleetMethodsApi,
  removeBankAccountGsnApi,
  serverBase,
} from '../../../../../../../constants/ApiConfigs';
import { deepGet, deepSet, removeEmptyKeys } from '../utils/dynamicFormUtils';
import {
  isWiseFormatResponse,
  isWiseMultiVariantResponse,
  normalizeWiseMultiVariantResponse,
  normalizeWiseResponseToSchema,
} from '../utils/wiseSchemaNormalizer';

/**
 * Build flat requirements object: keys = field.key, values from formData by path.
 * Chỉ lấy field có enabled === true để tránh gửi data sai / validate lỗi.
 * @param {Object} formData
 * @param {import('../utils/fieldSchemaTypes').FieldSchema[]} fields
 * @returns {Object}
 */
export function buildRequirementsFromFormData(formData, fields) {
  const out = {};
  if (!formData || !Array.isArray(fields)) return out;
  fields.forEach((item) => {
    if (item.enabled === false) return;
    const key = item.field?.key;
    if (!key) return;
    const value = deepGet(formData, item.path);
    if (value === undefined || value === null || value === '') return;
    out[key] = value;
  });
  return out;
}

/**
 * Build nested requirements object theo cấu trúc path (dùng cho create-bank-account-gsn).
 * Gồm cả field enabled: false (không hiển thị trên UI nhưng cần gửi khi submit, giá trị lấy từ default trong formData).
 * @param {Object} formData
 * @param {import('../utils/fieldSchemaTypes').FieldSchema[]} fields
 * @returns {Object}
 */
export function buildNestedRequirementsFromFormData(formData, fields) {
  const out = {};
  if (!formData || !Array.isArray(fields)) return out;
  fields.forEach((item) => {
    if (!item.path) return;
    let value = deepGet(formData, item.path);
    if (value === undefined || value === null) value = '';
    if (item.field?.key === 'transfer_method') {
      out.transfer_methods = Array.isArray(value) ? value : (value ? [value] : []);
      return;
    }
    deepSet(out, item.path, value);
  });
  return removeEmptyKeys(out);
}

/**
 * Create bank account / payout method – dùng chung cho Bank Transfer, Wise (tag), PayPal (identifier).
 * Tùy loại mà body gửi: wiseIdentifier, payPalIdentifier, wiseRequirementsData, airWallexRequirementsData.
 * @param {{
 *   fleetId: string,
 *   type: string,
 *   formData?: object,
 *   fields?: Array,
 *   gateway?: string,
 *   schema?: { type?: string, quoteId?: string },
 *   wiseIdentifier?: string,
 *   payPalIdentifier?: string
 * }} params
 * @returns {Promise<*>}
 */
export async function createBankAccountGsn(params) {
  const {
    fleetId,
    type = 'fleet',
    formData,
    fields = [],
    gateway,
    schema,
    wiseIdentifier,
    payPalIdentifier,
  } = params;
  const body = {
    fleetId,
    type,
  };
  const wiseId = (wiseIdentifier ?? '').toString().trim();
  const paypalId = (payPalIdentifier ?? '').toString().trim();
  if (wiseId) body.wiseIdentifier = wiseId;
  if (paypalId) body.payPalIdentifier = paypalId;

  const isWise = String(gateway || '').toLowerCase() === 'wise';
  if (gateway && formData && fields?.length) {
    if (isWise) {
      const detailsFields = fields.filter((f) => f.path !== 'accountHolderName');
      const details = buildNestedRequirementsFromFormData(formData, detailsFields);
      if (schema?.quoteId) body.quoteId = schema.quoteId;
      body.wiseRequirementsData = {
        ...(schema?.type && { type: schema.type }),
        accountHolderName: formData.accountHolderName ?? '',
        details,
      };
    } else {
      body.airWallexRequirementsData = buildNestedRequirementsFromFormData(formData, fields);
    }
  }

  const config = {
    method: 'POST',
    body: JSON.stringify(body),
  };
  const json = await callApiWithoutDispatch(createBankAccountGsnApi, config);
  if (json?.error) throw new Error(json.error?.message || json.error);
  return json;
}

/**
 * Remove payout method (bank account / Wise / PayPal).
 * @param {{ fleetId: string, token: string }} params
 * @returns {Promise<*>}
 */
export async function removeBankAccountGsn(params) {
  const { fleetId, token } = params;
  const config = {
    method: 'POST',
    body: JSON.stringify({ fleetId, token }),
  };
  const json = await callApiWithoutDispatch(removeBankAccountGsnApi, config);
  if (json?.error) throw new Error(json.error?.message || json.error);
  return json;
}

/**
 * Fetch list of payout methods for fleet (Wise, PayPal, AirWallex bank, etc.).
 * @param {{ fleetId: string }} params
 * @returns {Promise<Array<{ gateway: string, connectToken?: string, connectStatus?: string, transferMethod?: string }>>}
 */
export async function getPayoutFleetMethods(params) {
  const { fleetId } = params;
  const config = { method: 'POST', body: JSON.stringify({ fleetId }) };
  const json = await callApiWithoutDispatch(getPayoutFleetMethodsApi, config);
  if (json?.error) throw new Error(json.error?.message || json.error);
  const res = json?.res;
  return Array.isArray(res) ? res : [];
}

/**
 * Fetch dynamic required fields schema for bank account / payout form.
 * - AirWallex (refresh): airWallexRequirementsData = flat object (field.key -> value).
 * - Wise (refresh): `quoteId` ở root body (cùng fleetId/type); wiseRequirementsData = { type, accountHolderName, details } (không gửi quoteId trong object lồng nhau).
 * @param {{ fleetId: string, type: string, formData?: object, fields?: Array, gateway?: string, schema?: { type?: string, quoteId?: string } }} params
 * @returns {Promise<{ condition: object, fields: import('../utils/fieldSchemaTypes').FieldSchema[] }>}
 */
export async function getRequiredFieldsGsn(params) {
  const { fleetId, type = 'fleet', formData, fields = [], gateway, schema } = params;
  const body = {
    fleetId,
    type,
  };
  const isWise = String(gateway || '').toLowerCase() === 'wise';
  if (gateway && formData && fields?.length) {
    if (isWise && schema?.type) {
      const detailsFields = fields.filter((f) => f.path !== 'accountHolderName');
      const details = buildNestedRequirementsFromFormData(formData, detailsFields);
      if (schema.quoteId) body.quoteId = schema.quoteId;
      body.wiseRequirementsData = {
        type: schema.type,
        accountHolderName: formData.accountHolderName ?? '',
        details,
      };
    } else if (!isWise) {
      body.airWallexRequirementsData = buildRequirementsFromFormData(formData, fields);
    }
  }
  const config = {
    method: 'POST',
    body: JSON.stringify(body),
  };
  const json = await callApiWithoutDispatch(getRequiredFieldsGsnApi, config);
  if (json?.error) throw new Error(json.error.message || json.error);
  const raw = json?.res?.response ?? { condition: {}, fields: [] };
  if (isWiseMultiVariantResponse(raw)) return normalizeWiseMultiVariantResponse(raw);
  if (isWiseFormatResponse(raw)) return normalizeWiseResponseToSchema(raw);
  return raw;
}

/**
 * Fetch dynamic options for DYNAMIC_SELECT from query_url.
 * @param {{ query_url: string, method: string, query_params: Array<{ name?: string, value_of: string, required?: boolean }> }} dynamicOptions
 * @param {Object} formData
 * @param {function(Object, string):*} deepGet
 * @returns {Promise<Array<{ label: string, value: string }>>}
 */
export async function fetchDynamicSelectOptions(dynamicOptions, formData, deepGet) {
  if (!dynamicOptions?.query_url) return [];
  const params = {};
  (dynamicOptions.query_params || []).forEach((p) => {
    const key = p.name || p.key || p.param;
    const path = p.value_of;
    if (path && deepGet) {
      const val = deepGet(formData, path);
      if (val !== undefined && val !== null && val !== '') params[key] = val;
    }
  });
  const baseUrl = dynamicOptions.query_url.startsWith('http')
    ? ''
    : serverBase.replace(/\/$/, '');
  const path = dynamicOptions.query_url.startsWith('http')
    ? dynamicOptions.query_url
    : (dynamicOptions.query_url.startsWith('/') ? '' : '/') + dynamicOptions.query_url;
  const url = baseUrl + path;
  const method = (dynamicOptions.method || 'GET').toUpperCase();
  let fullUrl = url;
  let config = { method };
  if (method === 'GET') {
    const qs = new URLSearchParams(params).toString();
    fullUrl = qs ? `${url}${url.includes('?') ? '&' : '?'}${qs}` : url;
  } else {
    config.body = JSON.stringify(params);
  }
  const json = await callApiWithoutDispatch(fullUrl, config);
  const list = json?.data ?? json?.res ?? json ?? [];
  const arr = Array.isArray(list) ? list : (list.options || list.items || []);
  return arr.map((item) => (typeof item === 'object' && item !== null && 'value' in item ? item : { label: String(item), value: String(item) }));
}
